import { buildRelayablyInternalUrl } from '../api/relayablyInternalApi'
import { AuthMode, AuthProvider, AuthSession } from './types'

type SubmitAuthPayload = {
  mode: AuthMode
  method: AuthProvider
  email?: string
  password?: string
  fullName?: string
  phoneNumber?: string
  otp?: string
}

const request = async <T>(path: string, options: RequestInit = {}): Promise<T> => {
  const response = await fetch(buildRelayablyInternalUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-relayably-client': 'relayably-app',
      ...(options.headers ?? {}),
    },
  })

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: { message?: string } } | null
    throw new Error(body?.error?.message ?? 'Something went wrong while talking to Relayably.')
  }

  return (await response.json()) as T
}

export const relayablyApi = {
  async submitAuth(payload: SubmitAuthPayload): Promise<AuthSession> {
    return request<AuthSession>('/internal/auth/session', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async getMe(sessionToken: string): Promise<AuthSession> {
    return request<AuthSession>('/internal/auth/me', {
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    })
  },

  async logout(sessionToken: string) {
    return request<{ success: true }>('/internal/auth/logout', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    })
  },

  async linkProvider(sessionToken: string, provider: AuthProvider): Promise<AuthSession> {
    return request<AuthSession>('/internal/auth/link-provider', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
      body: JSON.stringify({ provider }),
    })
  },

  async unlinkProvider(sessionToken: string, provider: AuthProvider): Promise<AuthSession> {
    return request<AuthSession>('/internal/auth/unlink-provider', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
      body: JSON.stringify({ provider }),
    })
  },

  async exchangeFirebaseSession(idToken: string, provider: AuthProvider): Promise<AuthSession> {
    return request<AuthSession>('/internal/auth/firebase-session', {
      method: 'POST',
      body: JSON.stringify({
        idToken,
        provider,
      }),
    })
  },

  async signInWithGoogle(): Promise<AuthSession> {
    const authClient = require('@monorepo/utils/auth-client') as {
      signInWithGoogle: () => Promise<{ user?: { getIdToken: () => Promise<string> } }>
    }

    const result = await authClient.signInWithGoogle()
    const idToken = await result.user?.getIdToken()

    if (!idToken) {
      throw new Error('Google sign-in did not return a Firebase session.')
    }

    return this.exchangeFirebaseSession(idToken, 'google')
  },

  async signInWithLinkedIn(): Promise<void> {
    const authClient = require('@monorepo/utils/auth-client') as {
      signInWithLinkedIn: () => Promise<void>
    }

    await authClient.signInWithLinkedIn()
  },

  async completeLinkedInSignIn(): Promise<AuthSession> {
    const authClient = require('@monorepo/utils/auth-client') as {
      completeLinkedInSignIn: () => Promise<{ user?: { getIdToken: () => Promise<string> } }>
    }

    const result = await authClient.completeLinkedInSignIn()
    const idToken = await result.user?.getIdToken()

    if (!idToken) {
      throw new Error('LinkedIn sign-in did not return a Firebase session.')
    }

    return this.exchangeFirebaseSession(idToken, 'linkedin')
  },

  async signInWithApple(): Promise<AuthSession> {
    const authClient = require('@monorepo/utils/auth-client') as {
      signInWithApple: () => Promise<{ user?: { getIdToken: () => Promise<string> } }>
    }

    const result = await authClient.signInWithApple()
    const idToken = await result.user?.getIdToken()

    if (!idToken) {
      throw new Error('Apple sign-in did not return a Firebase session.')
    }

    return this.exchangeFirebaseSession(idToken, 'apple')
  },
}
